import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Form } from 'src/components/common/form/Form';
import { URLModal } from 'src/components/common/URLModal';
import Config from 'src/lib/config';
import { VALID_EMAIL } from 'src/lib/email';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  setOverlayClosedLocation,
  setOverlayVisibleLocation,
} from 'src/lib/routing';

export const EmailCardUserFragment = gql`
  fragment EmailCardUserFragment on User {
    id
    email
    canUpdateEmail
  }
`;

const ChangeEmailMutation = gql`
  mutation UI__EmailCardChangeEmailMutation($userId: ID!, $newEmail: String!) {
    user(id: $userId) {
      update(email: $newEmail) {
        id
        email
      }
    }
  }
`;

const validate = (newEmail: string) =>
  !newEmail
    ? 'Please provide a company email.'
    : !VALID_EMAIL.test(newEmail)
      ? 'Please provide a valid email address.'
      : null;

interface Props {
  user: GraphQLTypes.EmailCardUserFragment;
}
export function EmailCard({ user: { id, email, canUpdateEmail } }: Props) {
  const location = useLocation();
  const [newEmail, setNewEmail] = useState(email || '');
  const [error, setError] = useState<string | null>(null);
  const [changeEmail, { loading }] = useMutation<
    GraphQLTypes.UI__EmailCardChangeEmailMutation,
    GraphQLTypes.UI__EmailCardChangeEmailMutationVariables
  >(ChangeEmailMutation, {
    variables: { userId: id, newEmail },
    onCompleted: () => setOverlayClosedLocation(location),
    onError: (mutationError) =>
      setError(
        /E-mail address already in use/.test(mutationError.message)
          ? 'An organization with this email address already exists. Please use a different email address.'
          : 'Error updating email',
      ),
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Company email</CardTitle>
          <CardDescription>{email || 'Unknown'}</CardDescription>
          <Button
            variant="secondary"
            isDisabled={!canUpdateEmail}
            as={Link}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.updateUserEmail,
            })}
          >
            Update email
          </Button>
        </CardHeader>
      </Card>
      <URLModal name={Config.modals.updateUserEmail}>
        {({ isOpen, closeModal }) => {
          const handleModalClose = () => {
            setError(null);
            setNewEmail(email || '');
            closeModal();
          };
          return (
            <Modal isOpen={isOpen} onClose={handleModalClose} size="xl">
              <ModalOverlay />
              <Form
                onSubmit={() => {
                  const validationError = validate(newEmail);
                  if (validationError) {
                    setError(validationError);
                    return;
                  }
                  setError(null);
                  changeEmail();
                  closeModal();
                }}
                noValidate={true}
              >
                <ModalContent>
                  <ModalHeader>
                    <ModalTitle>Update Company Email</ModalTitle>
                  </ModalHeader>
                  <ModalBody>
                    <FormControl isInvalid={!!error}>
                      <FormLabel>Company email</FormLabel>
                      <Input
                        value={newEmail}
                        placeholder={email || ''}
                        autoFocus
                        onChange={(e) => {
                          setError(null);
                          setNewEmail(e.target.value);
                        }}
                      />
                      <FormErrorMessage>{error}</FormErrorMessage>
                    </FormControl>
                  </ModalBody>
                  <ModalFooter>
                    <ButtonGroup>
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={handleModalClose}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        isLoading={loading}
                        loadingText="Update email"
                      >
                        Update email
                      </Button>
                    </ButtonGroup>
                  </ModalFooter>
                </ModalContent>
              </Form>
            </Modal>
          );
        }}
      </URLModal>
    </>
  );
}
